import { Alert, AlertTitle, Button, Typography } from '@mui/material';
import FlexContainer from 'components/FlexContainer';
import { YellowCrownImage } from 'components/icons/YellowCrownImage';
import { PricingRestrictionHint } from 'features/aiWriter/AiWriterSidebar/steps/PricingRestrictionHint';
import useEditorText from 'features/aiWriter/AiWriterTextEditor/hooks/useEditorText';
import { useEditorWordsCount } from 'features/aiWriter/AiWriterTextEditor/hooks/useEditorWordsCount';
import {
  getCurrentModelLanguageAndCountry,
  getGeneratingTextInEditor
} from 'features/aiWriter/store/selectors';
import { ClaimDetailView } from 'features/fact-grabbing/ClaimDetailView';
import { ClaimList } from 'features/fact-grabbing/ClaimList';
import { RefreshCard } from 'features/fact-grabbing/RefreshCard';
import { useFactGrabbingService } from 'features/fact-grabbing/useFactGrabbingService';
import { useFactGrabbingStore } from 'features/fact-grabbing/useFactGrabbingStore';
import { getGetIsFeatureAvailable } from 'features/features/store/selectors';
import FormattedMessage from 'features/i18n/FormattedMessage';
import { countryLabelSelector, languageLabelSelector } from 'features/language/store/selectors';
import useUpdateSubscriptionModal from 'features/updateSubscriptionModal/hook/useUpdateSubscriptionModal';
import { useEffect, useState } from 'react';
import { ApiClaim } from 'services/backofficeIntegration/http/endpoints/fact-grabbing/httpGetClaimsByText';
import gtmIds from 'services/tracking/GTMIds';
import { useAppSelector } from 'store/hooks';
import styled from 'styled-components';

// Defined by product requirements
const minWordCount = 3;
// If the document contains more words, we only show a refresh button instead of auto-updating
const maxWordCountForAutoRefresh = 1000;
// Max number of words to enable the feature
const maxWordCount = 1000;

export const FactGrabbingView = () => {
  const isFactGrabbingAvailable = useAppSelector(getGetIsFeatureAvailable)('factGrabbing');
  const editorText = useEditorText();

  const { currentModelLanguage: documentLanguage, currentModelCountry: documentCountry } =
    useAppSelector(getCurrentModelLanguageAndCountry);
  const getLanguageLabel = useAppSelector(languageLabelSelector);
  const getCountryLabel = useAppSelector(countryLabelSelector);
  const isInEditorGenerationActive = !!useAppSelector(getGeneratingTextInEditor);

  const editorTextWordCount = useEditorWordsCount();
  const hasForbiddingLanguage = documentLanguage !== 'de' || documentCountry !== 'de';

  const showUpgradeSubscriptionModal = useUpdateSubscriptionModal();

  const hasLessThanMinWordCount = editorTextWordCount < minWordCount;
  const hasMoreThanMaxWordCountForAutoRun = editorTextWordCount > maxWordCountForAutoRefresh;
  const hasMoreThanMaxWordCount = editorTextWordCount > maxWordCount;
  const isAutoRunningDisabled =
    !isFactGrabbingAvailable ||
    hasLessThanMinWordCount ||
    hasMoreThanMaxWordCountForAutoRun ||
    hasForbiddingLanguage ||
    isInEditorGenerationActive;

  const getCachedClaims = useFactGrabbingStore(state => state.getCachedClaims);
  const isFetchingClaims = useFactGrabbingStore(state => state.isLoading);
  const isError = useFactGrabbingStore(state => state.hasError);

  const { fetchClaims } = useFactGrabbingService();

  const claims = getCachedClaims(editorText);

  const [selectedClaim, setSelectedClaim] = useState<ApiClaim | null>(null);
  const [hasFetchedAtLeastOnce, setHasFetchedAtLeastOnce] = useState(claims && claims.length > 0);

  // Run fetch once on mount, if available
  useEffect(() => {
    if (
      isAutoRunningDisabled ||
      !editorText ||
      editorText.trim().length < minWordCount ||
      (claims && claims.length > 0) ||
      isFetchingClaims ||
      hasFetchedAtLeastOnce
    ) {
      return;
    }

    setHasFetchedAtLeastOnce(true);
    fetchClaims({ text: editorText });
  }, [
    isAutoRunningDisabled,
    editorText,
    fetchClaims,
    claims,
    isFetchingClaims,
    hasFetchedAtLeastOnce
  ]);

  const handleRefetchClaims = () => {
    if (!editorText || editorText.trim().length < minWordCount) {
      return;
    }

    setHasFetchedAtLeastOnce(true);
    fetchClaims({ text: editorText, noCache: true });
  };

  const handleClaimClick = (claim: ApiClaim) => {
    setSelectedClaim(claim);
  };

  const handleBackToOverview = () => {
    setSelectedClaim(null);
  };

  if (!isFactGrabbingAvailable) {
    return (
      <FlexContainer gap="four">
        <PricingRestrictionHint title="aiWriter.inspirations.performance_step.factGrabbing.restriction.title" />
        <Button
          id={gtmIds.aiWriter.factGrabbing.openTab}
          variant="contained"
          color="primary"
          type="submit"
          fullWidth={true}
          startIcon={<YellowCrownImage />}
          onClick={() => showUpgradeSubscriptionModal()}
        >
          <FormattedMessage id="common.upgrade" />
        </Button>
      </FlexContainer>
    );
  }

  if (hasForbiddingLanguage) {
    return (
      <Alert severity="info" color="error">
        <AlertTitle>
          <FormattedMessage id="aiWriter.inspirations.factGrabbing.initial_view.error_title_analysis_unavailable" />
        </AlertTitle>

        <FormattedMessage
          id="aiWriter.inspirations.factGrabbing.initial_view.error_forbidden_language"
          values={{
            language: `${getLanguageLabel(documentLanguage)} (${getCountryLabel(documentCountry)})`
          }}
        />
      </Alert>
    );
  }

  if (hasLessThanMinWordCount) {
    return (
      <Alert severity="info" color="error">
        <AlertTitle>
          <FormattedMessage id="aiWriter.inspirations.factGrabbing.initial_view.error_min_word_count_title" />
        </AlertTitle>

        <FormattedMessage
          id="aiWriter.inspirations.factGrabbing.initial_view.error_min_word_count"
          values={{
            minWordCount: minWordCount.toLocaleString(),
            wordCount: editorTextWordCount.toLocaleString()
          }}
        />
      </Alert>
    );
  }

  if (hasMoreThanMaxWordCount) {
    return (
      <Alert severity="info" color="error">
        <AlertTitle>
          <FormattedMessage id="aiWriter.inspirations.factGrabbing.initial_view.error_min_word_count_title" />
        </AlertTitle>

        <FormattedMessage
          id="aiWriter.inspirations.factGrabbing.initial_view.error_max_word_count"
          values={{
            maxWordCount: maxWordCount.toLocaleString(),
            wordCount: editorTextWordCount.toLocaleString()
          }}
        />
      </Alert>
    );
  }

  return (
    <ClaimListContainer>
      {selectedClaim ? (
        <ClaimDetailView claim={selectedClaim} onBackClick={handleBackToOverview} />
      ) : (
        <>
          <RefreshCard onRefreshClick={handleRefetchClaims} disabled={isFetchingClaims} />

          {!hasFetchedAtLeastOnce && !isFetchingClaims ? (
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage id="aiWriter.inspirations.factGrabbing.initial_view.empty_use_manual_button" />
            </Typography>
          ) : (
            <ClaimList
              claims={claims || []}
              isLoading={isFetchingClaims}
              isError={isError}
              onClaimClick={handleClaimClick}
            />
          )}
        </>
      )}
    </ClaimListContainer>
  );
};

const ClaimListContainer = styled(FlexContainer).attrs({
  direction: 'column',
  gap: 'three'
})`
  width: 100%;
`;
